import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, Mail, Send, RotateCw, Search, CheckCircle, XCircle, Ban,
  Pause, Play, User, ChevronDown, ArrowLeft, Eye, MousePointerClick, RefreshCw,
} from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const API_URL = window.location.origin;

const TEMPLATES = [
  { key: 'complete_your_stall',   label: 'Complete Your Stall' },
  { key: 're_engagement',         label: 'Re-engagement (30d no login)' },
  { key: 'trial_reminder_7',      label: 'Trial Reminder — 7 days left' },
  { key: 'trial_reminder_1',      label: 'Trial Reminder — 1 day left' },
  { key: 'trial_expired',         label: 'Trial Expired' },
  { key: 'clients_miss_you',      label: 'Your Customers Miss You' },
  { key: 'subscribe_reminder',    label: 'Subscribe Reminder' },
  { key: 'cash_renewal_reminder', label: 'Cash Renewal Reminder' },
  { key: 'account_approved',      label: 'Account Approved' },
  { key: 'account_declined',      label: 'Account Declined' },
];

const STATUS_BADGE = {
  sent:       { bg: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', Icon: CheckCircle },
  failed:     { bg: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',                   Icon: XCircle },
  suppressed: { bg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',         Icon: Ban },
};

export const EmailDashboardModal = ({ isOpen, onClose, token, allVendors = [], setMessage }) => {
  // ───── MODES ─────
  // 'browse'  → global table & filters
  // 'vendor'  → focused on a single vendor (selectedVendor)
  const [mode, setMode] = useState('browse');
  const [selectedVendor, setSelectedVendor] = useState(null);

  // Global table state
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Vendor search dropdown
  const [vendorQuery, setVendorQuery] = useState('');
  const [vendorOpen, setVendorOpen] = useState(false);
  const filteredVendors = useMemo(() => {
    const q = vendorQuery.trim().toLowerCase();
    if (!q) return allVendors.slice(0, 30);
    return allVendors.filter((v) =>
      (v.email || '').toLowerCase().includes(q) ||
      (v.name || '').toLowerCase().includes(q) ||
      (v.first_name || '').toLowerCase().includes(q) ||
      (v.last_name || '').toLowerCase().includes(q),
    ).slice(0, 30);
  }, [vendorQuery, allVendors]);

  // Ad-hoc send (vendor mode only)
  const [adhocTemplate, setAdhocTemplate] = useState('complete_your_stall');
  const [busy, setBusy] = useState(false);
  const [resyncing, setResyncing] = useState(false);

  const handleResyncFromResend = async () => {
    if (!window.confirm('Re-sync open/click status from Resend? This pulls the latest events for every email.')) return;
    setResyncing(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/emails/backfill-from-resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (resp.ok) {
        setMessage?.({
          type: 'success',
          text: `Synced ${data.scanned} emails (${data.imported} new, ${data.skipped_existing} updated)`,
        });
        fetchLog();
      } else {
        setMessage?.({ type: 'error', text: data.detail || 'Re-sync failed' });
      }
    } finally {
      setResyncing(false);
    }
  };

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (mode === 'vendor' && selectedVendor) {
        params.append('to_email', selectedVendor.email.toLowerCase());
        params.append('limit', '200');
      } else {
        params.append('limit', '100');
      }
      const resp = await fetch(`${API_URL}/api/admin/emails/log?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (resp.ok) {
        setRows(data.rows || []);
        setStats(data.stats_30d || {});
        setTotal(data.total || 0);
      } else {
        setMessage?.({ type: 'error', text: data.detail || 'Failed to load email log' });
      }
    } catch {
      setMessage?.({ type: 'error', text: 'Network error loading email log' });
    } finally {
      setLoading(false);
    }
  }, [mode, selectedVendor, token, setMessage]);

  useEffect(() => {
    if (isOpen) fetchLog();
  }, [isOpen, fetchLog]);

  // Refresh selectedVendor object whenever allVendors changes
  useEffect(() => {
    if (selectedVendor) {
      const fresh = allVendors.find((v) => v._id === selectedVendor._id);
      if (fresh) setSelectedVendor(fresh);
    }
  }, [allVendors, selectedVendor]);

  const handleSelectVendor = (v) => {
    setSelectedVendor(v);
    setMode('vendor');
    setVendorOpen(false);
    setVendorQuery('');
  };

  const handleBack = () => {
    setMode('browse');
    setSelectedVendor(null);
  };

  const handleResend = async (row) => {
    if (!window.confirm(`Resend "${row.subject}" to ${row.to_email}?`)) return;
    setBusy(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/emails/resend/${row._id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (resp.ok && data.status === 'ok') {
        setMessage?.({ type: 'success', text: `Resent to ${row.to_email}` });
        fetchLog();
      } else {
        setMessage?.({ type: 'error', text: data.detail || 'Resend failed' });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleAdhocSend = async () => {
    if (!selectedVendor || !adhocTemplate) return;
    setBusy(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/emails/send-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: selectedVendor._id, template: adhocTemplate }),
      });
      const data = await resp.json();
      if (resp.ok && data.status === 'ok') {
        setMessage?.({ type: 'success', text: `Sent "${adhocTemplate}" to ${data.to}` });
        fetchLog();
      } else {
        setMessage?.({ type: 'error', text: data.detail || 'Send failed' });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleTogglePref = async (kind) => {
    if (!selectedVendor) return;
    setBusy(true);
    try {
      const body =
        kind === 'all'
          ? { unsubscribed_all: !selectedVendor.unsubscribed_all }
          : { [kind]: !(selectedVendor.email_preferences?.[kind] ?? true) };
      const resp = await fetch(`${API_URL}/api/admin/users/${selectedVendor._id}/email-preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (resp.ok) {
        setSelectedVendor({ ...selectedVendor, ...data.user });
        setMessage?.({ type: 'success', text: 'Preferences updated' });
      } else {
        setMessage?.({ type: 'error', text: data.detail || 'Update failed' });
      }
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="email-dashboard-modal">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b dark:border-gray-700">
          <div className="flex items-center gap-3">
            {mode === 'vendor' && (
              <button onClick={handleBack} className="text-gray-500 hover:text-[#FFD12D]" data-testid="email-back-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="w-10 h-10 rounded-xl bg-[#FFD12D]/20 flex items-center justify-center">
              <Mail className="w-5 h-5 text-[#FFD12D]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#252525] dark:text-white">
                {mode === 'vendor' ? 'Vendor Email History' : 'Email Management'}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {mode === 'vendor' ? selectedVendor?.email : 'Search a vendor or browse recent activity'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-[#FFD12D]" data-testid="email-dashboard-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Re-sync from Resend banner */}
        <div className="px-5 py-2 border-b dark:border-gray-700 bg-amber-50 dark:bg-amber-900/10 flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs text-amber-900 dark:text-amber-200">
            <strong>Tip:</strong> Open & click data comes from Resend. Click "Re-sync" to pull the latest events.
          </p>
          <Button
            onClick={handleResyncFromResend}
            disabled={resyncing}
            variant="outline"
            size="sm"
            className="bg-white dark:bg-[#1a1a1a]"
            data-testid="email-resync-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${resyncing ? 'animate-spin' : ''}`} />
            {resyncing ? 'Syncing…' : 'Re-sync from Resend'}
          </Button>
        </div>

        {/* Vendor search bar — always visible at top */}
        {mode === 'browse' && (
          <div className="px-5 py-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-[#252525]">
            <label className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Search a vendor</label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Type an email, name, or company…"
                value={vendorQuery}
                onChange={(e) => { setVendorQuery(e.target.value); setVendorOpen(true); }}
                onFocus={() => setVendorOpen(true)}
                className="pl-9 dark:bg-[#1a1a1a] dark:text-white"
                data-testid="email-vendor-search"
              />
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />

              {vendorOpen && filteredVendors.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-[#1a1a1a] border dark:border-gray-700 rounded-lg shadow-lg max-h-72 overflow-auto" data-testid="email-vendor-results">
                  {filteredVendors.map((v) => (
                    <button
                      key={v._id}
                      onClick={() => handleSelectVendor(v)}
                      className="w-full text-left px-3 py-2 hover:bg-[#FFD12D]/10 dark:hover:bg-[#FFD12D]/20 flex items-center gap-2 border-b dark:border-gray-800 last:border-0"
                      data-testid={`email-vendor-pick-${v._id}`}
                    >
                      <User className="w-4 h-4 text-gray-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate dark:text-white">{v.email}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {v.name || `${v.first_name || ''} ${v.last_name || ''}`.trim() || '—'}
                          {v.subscription ? ` · ${v.subscription}` : ''}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Or scroll down to browse the most recent 100 emails across all vendors.
            </p>
          </div>
        )}

        {/* Stats strip (browse only) */}
        {mode === 'browse' && (
          <div className="grid grid-cols-4 gap-3 px-5 py-3 border-b dark:border-gray-700">
            <Stat label="Sent (30d)"       value={stats.sent || 0}       tone="emerald" Icon={CheckCircle} />
            <Stat label="Failed (30d)"     value={stats.failed || 0}     tone="red"     Icon={XCircle} />
            <Stat label="Suppressed (30d)" value={stats.suppressed || 0} tone="amber"   Icon={Ban} />
            <Stat label="Total in log"     value={total}                 tone="gray"    Icon={Mail} />
          </div>
        )}

        {/* Vendor-focused control panel */}
        {mode === 'vendor' && selectedVendor && (
          <div className="p-5 border-b dark:border-gray-700 bg-gray-50 dark:bg-[#252525] space-y-4">
            {/* Vendor card */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-lg font-bold text-[#252525] dark:text-white">
                  {selectedVendor.name || `${selectedVendor.first_name || ''} ${selectedVendor.last_name || ''}`.trim() || selectedVendor.email}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{selectedVendor.email}</div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Tag label={`subscription: ${selectedVendor.subscription || 'none'}`} />
                  <Tag label={`status: ${selectedVendor.status || '—'}`} />
                  {selectedVendor.unsubscribed_all && <Tag label="Unsubscribed ALL" tone="red" />}
                </div>
              </div>
              <div className="text-right text-xs text-gray-500 dark:text-gray-400">
                <div>{total} email{total !== 1 ? 's' : ''} sent</div>
              </div>
            </div>

            {/* Preferences toggles */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <PrefRow
                label="Marketing & reminders"
                on={selectedVendor.email_preferences?.marketing !== false && !selectedVendor.unsubscribed_all}
                onClick={() => handleTogglePref('marketing')}
                busy={busy}
                testId="email-vendor-pref-marketing"
              />
              <PrefRow
                label="Billing alerts"
                on={selectedVendor.email_preferences?.billing !== false && !selectedVendor.unsubscribed_all}
                onClick={() => handleTogglePref('billing')}
                busy={busy}
                testId="email-vendor-pref-billing"
              />
              <PrefRow
                label="ALL emails"
                on={!selectedVendor.unsubscribed_all}
                onClick={() => handleTogglePref('all')}
                busy={busy}
                tone="danger"
                testId="email-vendor-pref-all"
              />
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 -mt-2">
              Transactional emails (password reset, account approvals) are always sent.
            </p>

            {/* Ad-hoc send */}
            <div className="bg-white dark:bg-[#1a1a1a] border dark:border-gray-700 rounded-lg p-3">
              <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">
                Send a template to this vendor
              </div>
              <div className="flex flex-col md:flex-row gap-2">
                <select
                  value={adhocTemplate}
                  onChange={(e) => setAdhocTemplate(e.target.value)}
                  className="flex-1 p-2 rounded border dark:bg-[#252525] dark:border-gray-600 dark:text-white text-sm"
                  data-testid="email-vendor-adhoc-template"
                >
                  {TEMPLATES.map((t) => (<option key={t.key} value={t.key}>{t.label}</option>))}
                </select>
                <Button
                  onClick={handleAdhocSend}
                  disabled={busy}
                  className="bg-[#FFD12D] hover:bg-[#FFE066] text-[#252525]"
                  data-testid="email-vendor-adhoc-send"
                >
                  <Send className="w-4 h-4 mr-1" /> Send Now
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Log table */}
        <div className="flex-1 overflow-auto p-5">
          {loading ? (
            <p className="text-center text-gray-500 py-12">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-center text-gray-500 py-12">
              {mode === 'vendor' ? `No emails sent to ${selectedVendor?.email} yet.` : 'No emails matching your filters.'}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b dark:border-gray-700 sticky top-0 bg-white dark:bg-[#1a1a1a]">
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="py-2 px-2">When</th>
                  {mode !== 'vendor' && <th className="py-2 px-2">Recipient</th>}
                  <th className="py-2 px-2">Subject</th>
                  <th className="py-2 px-2">Category</th>
                  <th className="py-2 px-2">Status</th>
                  <th className="py-2 px-2 text-center">Engaged</th>
                  <th className="py-2 px-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const badge = STATUS_BADGE[r.status] || STATUS_BADGE.sent;
                  return (
                    <tr key={r._id} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-[#252525]">
                      <td className="py-2 px-2 text-xs text-gray-500 whitespace-nowrap">
                        {r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}
                      </td>
                      {mode !== 'vendor' && (
                        <td className="py-2 px-2 dark:text-white">
                          <button
                            onClick={() => {
                              const v = allVendors.find((vv) => vv.email?.toLowerCase() === r.to_email);
                              if (v) handleSelectVendor(v);
                            }}
                            className="hover:underline text-left"
                          >
                            {r.to_email}
                          </button>
                        </td>
                      )}
                      <td className="py-2 px-2 dark:text-white">{r.subject}</td>
                      <td className="py-2 px-2 text-gray-600 dark:text-gray-400">{r.category}</td>
                      <td className="py-2 px-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${badge.bg}`}>
                          <badge.Icon className="w-3 h-3" /> {r.status}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-center">
                        <div className="inline-flex gap-1.5">
                          {r.clicked ? (
                            <span title="Clicked a link" className="text-blue-600 dark:text-blue-400">
                              <MousePointerClick className="w-3.5 h-3.5" />
                            </span>
                          ) : r.opened ? (
                            <span title="Opened" className="text-emerald-600 dark:text-emerald-400">
                              <Eye className="w-3.5 h-3.5" />
                            </span>
                          ) : (
                            <span className="text-gray-300 dark:text-gray-700" title="Not opened yet">
                              <Eye className="w-3.5 h-3.5" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right">
                        {r.status !== 'suppressed' && (
                          <button
                            onClick={() => handleResend(r)}
                            disabled={busy}
                            className="text-xs text-[#FFD12D] hover:underline"
                            data-testid={`email-resend-${r._id}`}
                          >
                            <RotateCw className="w-3 h-3 inline mr-1" /> Resend
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, tone, Icon }) => {
  const toneMap = {
    emerald: 'text-emerald-700 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300',
    red:     'text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300',
    amber:   'text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300',
    gray:    'text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-200',
  };
  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-3 flex items-center gap-3 border dark:border-gray-700">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${toneMap[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-lg font-bold text-[#252525] dark:text-white leading-none">{value}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      </div>
    </div>
  );
};

const Tag = ({ label, tone = 'gray' }) => {
  const toneMap = {
    gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    red:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full ${toneMap[tone]}`}>{label}</span>;
};

const PrefRow = ({ label, on, onClick, busy, tone = 'default', testId }) => (
  <div className={`flex items-center justify-between p-3 rounded-lg border dark:border-gray-700 bg-white dark:bg-[#1a1a1a] ${tone === 'danger' ? 'border-red-300/40' : ''}`}>
    <div>
      <div className="text-sm font-medium text-[#252525] dark:text-white">{label}</div>
      <div className={`text-xs ${on ? 'text-emerald-600' : 'text-gray-500'}`}>{on ? 'Receiving' : 'Paused'}</div>
    </div>
    <Button onClick={onClick} disabled={busy} variant="outline" size="sm" data-testid={testId}>
      {on ? <><Pause className="w-3.5 h-3.5 mr-1" /> Pause</> : <><Play className="w-3.5 h-3.5 mr-1" /> Resume</>}
    </Button>
  </div>
);

export default EmailDashboardModal;
